import type { Row } from "@tanstack/react-table";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ErrorContainer,
  IconCross,
  IconTick,
  Spinner,
  TableRowSubComponent,
  Text,
} from "@sentricmusic/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { Writer } from "@/api/queries";
import type { WriterSplit } from "@/features/songs/hooks/types";

import { useGetMySongsDetailed, useGetSocieties } from "@/api/queries";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

type WriterDetailsProps = {
  row: Row<Writer>;
};

function WriterDetails({ row }: WriterDetailsProps) {
  const { t } = useTranslation();
  const { original } = row;

  const isTunecore = useUIStateStore(state => state.isTunecore);

  let proName: string | undefined;

  const societies = useGetSocieties();
  const selectedAccount = useAppStore(s => s.selectedAccount);

  if (societies.isSuccess && societies?.data != null && original.societycisaccode != null) {
    const pro = societies.data.societies.find(s => s.cisaccode === original.societycisaccode);
    proName = pro?.name;
  }

  function caeipiNumberFormatter(number: string | undefined): string {
    if (number == null || number === "")
      return "";
    switch (number) {
      case "1": return "Registered";
      case "2": return "Registration Pending";
      case "3": return "Registration Failed";
      default: return number;
    }
  }

  const songs = useGetMySongsDetailed({ aac: selectedAccount as string, wc: original.writercode });

  const designationCodes = useMemo(() => [
    { name: t("songs:author-option"), value: 1 },
    { name: t("songs:composer-option"), value: 2 },
    { name: t("songs:both-option"), value: 3 },
  ], [t]);

  const songRows = useMemo(() => {
    if (!songs.data?.works)
      return [];
    return songs.data.works.map((w) => {
      const splits = (w.writersplits as WriterSplit[] | undefined) ?? [];
      const split = splits.find(s => s.writercode === original.writercode);
      return {
        title: w.title,
        split: split?.writershare ?? 0,
        righttocollect: split?.righttocollect ?? false,
        designation: designationCodes.find(d => d.value === split?.writerdesignationcode)?.name ?? "",
      };
    });
  }, [songs.data, original.writercode, designationCodes]);

  return (
    <>
      <TableRowSubComponent>
        <Text as="subtitle">
          {[original?.firstname, original?.middlename, original?.lastname].filter(Boolean).join(" ")}
        </Text>
        <div className="flex flex-row gap-8">
          <p className="text-xs flex flex-col gap-1">
            <span className="font-semibold uppercase">{t("writers:_membership", "Membership")}</span>
            <span>{proName ?? ""}</span>
          </p>
          <p className="text-xs flex flex-col gap-1">
            <span className="font-semibold uppercase">CAE / IPI #</span>
            <span>{caeipiNumberFormatter(original.caeipinumber)}</span>
          </p>
          {isTunecore && (
            <p className="text-xs flex flex-col gap-1">
              <span className="font-semibold uppercase">{t("writers:dob", "DOB")}</span>
              <span>{original.dateofbirth?.split("T")[0] ?? ""}</span>
            </p>
          )}
        </div>
        <Accordion type="single" collapsible defaultValue="writer-songs">
          <AccordionItem value="writer-songs" className="px-2 transition-colors duration-200 ease-out rounded-lg">
            <AccordionTrigger>
              <Text as="subtitle">{t("writers:songs-written-header", "Songs Written")}</Text>
            </AccordionTrigger>
            <AccordionContent>
              {songs.isLoading
                ? <Spinner />
                : songs.isError
                  ? <ErrorContainer message={String(t("writers:error-loading-songs", "Failed to load songs. Try closing and reopening this row."))} />
                  : songRows.length === 0
                    ? <Text>{t("writers:no-songs-available-for-writer", "No songs available for writer")}</Text>
                    : (
                        <div className="space-y-1">
                          <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-8 text-xs font-semibold uppercase pb-2 border-b">
                            <span>{t("writers:_title", "Title")}</span>
                            <span>{t("writers:writer-split", "Writer Split")}</span>
                            <span>{t("writers:right-to-collect-col", "Right to Collect")}</span>
                            <span>{t("writers:designation-code", "Designation Code")}</span>
                          </div>
                          {songRows.map((song, i) => (
                            <div key={i} className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-8 text-sm py-2 border-b border-gray-300">
                              <span>{song.title}</span>
                              <span>
                                {song.split}
                                %
                              </span>
                              <span>
                                {song.righttocollect
                                  ? <IconTick size="md" className="inline" style={{ stroke: "var(--color-stroke-success)" }} />
                                  : <IconCross size="md" className="inline" style={{ stroke: "var(--color-stroke-error)" }} />}
                              </span>
                              <span>{song.designation}</span>
                            </div>
                          ))}
                        </div>
                      )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </TableRowSubComponent>
    </>
  );
}

export default WriterDetails;
